// Grill Flip — Big T's sizzling minigame.
// Patties land on the grill. A sear meter rises; click to flip in the green zone.
// Flip too early (raw) or too late (burnt) and the patty is ruined. Plate perfect
// patties for points and tips. Costs energy/time like Order Rush.

const DURATION = 60;
const MAX_PATTIES = 4;

export class GrillFlip {
  constructor({ onFlip, onPlate, onFinish } = {}){
    this.onFlip = onFlip || (() => {});
    this.onPlate = onPlate || (() => {});
    this.onFinish = onFinish || (() => {});
    this.active = false;
    this.finished = false;

    this.timeLeft = DURATION;
    this.spawnT = 2.0;
    this.flipped = 0;
    this.burnt = 0;
    this.raw = 0;
    this.plated = 0;
    this.points = 0;
    this.combo = 0;
    this.bestCombo = 0;

    this.patties = []; // { el, state: 'raw'|'flipped'|'plated'|'ruined', sear: 0..1, side, id }
    this._onKey = this._onKey.bind(this);
  }

  mount(root){
    const el = document.createElement('div');
    el.className = 'grill-hud';
    el.innerHTML = `
      <div class="grill-info">
        <span class="pill grill-timer">1:00</span>
        <span class="pill grill-plated">0 plated</span>
        <span class="pill grill-combo" hidden></span>
      </div>
      <div class="grill-grate" id="grill-grate"></div>
      <div class="grill-feedback" id="grill-feedback"></div>
      <div class="grill-help">click a patty (or press 1-4) to flip when the sear is in the green zone. flip again to plate.</div>
    `;
    (root || document.body).appendChild(el);
    this.el = el;
    this.grateEl = el.querySelector('#grill-grate');
    this.timerEl = el.querySelector('.grill-timer');
    this.platedEl = el.querySelector('.grill-plated');
    this.comboEl = el.querySelector('.grill-combo');
    this.feedbackEl = el.querySelector('#grill-feedback');
    window.addEventListener('keydown', this._onKey);
  }

  begin(){ this.active = true; this._spawn(); }

  _onKey(e){
    if (!this.active) return;
    const n = parseInt(e.key, 10);
    if (n >= 1 && n <= Math.min(MAX_PATTIES, this.patties.length)){
      this._flipPatty(this.patties[n - 1]);
    }
  }

  _spawn(){
    if (this.patties.length >= MAX_PATTIES) return;
    const id = Math.random().toString(36).slice(2, 7);
    const el = document.createElement('button');
    el.className = 'grill-patty in';
    el.innerHTML = `
      <div class="g-sear"><i></i></div>
      <div class="g-face">🥩</div>
    `;
    const patty = {
      id,
      el,
      state: 'raw',
      sear: 0,
      searSpeed: 0.18 + Math.random() * 0.12,
      flipSear: 0.45 + Math.random() * 0.2, // target window center
      flipWindow: 0.14,
      side: 0, // 0 = first side, 1 = second side
      ruined: false
    };
    el.addEventListener('pointerdown', (e) => { e.preventDefault(); this._flipPatty(patty); });
    this.grateEl.appendChild(el);
    this.patties.push(patty);
  }

  _flipPatty(patty){
    if (!this.active || patty.ruined || patty.state === 'plated') return;

    if (patty.state === 'raw'){
      // first flip
      const diff = Math.abs(patty.sear - patty.flipSear);
      if (diff <= patty.flipWindow){
        patty.state = 'flipped';
        patty.sear = 0;
        patty.side = 1;
        patty.el.classList.add('flipped');
        this.flipped++;
        this.combo++;
        this.bestCombo = Math.max(this.bestCombo, this.combo);
        this._feedback('perfect flip! 🔥', 'good');
        this.onFlip({ perfect: true });
      } else if (patty.sear < patty.flipSear - patty.flipWindow){
        patty.ruined = true;
        patty.el.classList.add('ruined', 'raw-ruin');
        this.raw++;
        this.combo = 0;
        this._feedback('too raw!', 'bad');
      } else {
        patty.ruined = true;
        patty.el.classList.add('ruined', 'burnt-ruin');
        this.burnt++;
        this.combo = 0;
        this._feedback('burnt!', 'bad');
      }
      this._renderScore();
      return;
    }

    if (patty.state === 'flipped'){
      // plate it
      const diff = Math.abs(patty.sear - patty.flipSear);
      if (diff <= patty.flipWindow){
        patty.state = 'plated';
        patty.el.classList.add('plated');
        this.plated++;
        this.combo++;
        this.bestCombo = Math.max(this.bestCombo, this.combo);
        this.points += 8 + Math.min(this.combo, 10);
        this._feedback('plated! ✓', 'good');
        this.onPlate({ perfect: true });
      } else if (patty.sear < patty.flipSear - patty.flipWindow){
        patty.ruined = true;
        patty.el.classList.add('ruined', 'raw-ruin');
        this.raw++;
        this.combo = 0;
        this._feedback('undercooked!', 'bad');
      } else {
        patty.ruined = true;
        patty.el.classList.add('ruined', 'burnt-ruin');
        this.burnt++;
        this.combo = 0;
        this._feedback('burnt!', 'bad');
      }
      this._renderScore();
    }
  }

  update(dt){
    if (!this.active || this.finished) return;

    this.timeLeft -= dt;
    if (this.timeLeft <= 0){ this.timeLeft = 0; this._renderTimer(); return this._finish(); }
    this._renderTimer();

    this.spawnT -= dt;
    if (this.spawnT <= 0){ this.spawnT = 2.5 + Math.random() * 2.0; this._spawn(); }

    for (let i = this.patties.length - 1; i >= 0; i--){
      const p = this.patties[i];
      if (p.state === 'plated' || p.ruined) continue;
      p.sear += dt * p.searSpeed;
      if (p.sear >= 1){
        p.sear = 1;
        if (!p.ruined){
          p.ruined = true;
          p.el.classList.add('ruined', 'burnt-ruin');
          this.burnt++;
          this.combo = 0;
          this._feedback('left too long — burnt!', 'bad');
          this._renderScore();
        }
      }
      const bar = p.el.querySelector('.g-sear > i');
      if (bar) bar.style.width = `${p.sear * 100}%`;
      // tint patty darker as it sears
      const face = p.el.querySelector('.g-face');
      if (face){
        const cooked = Math.min(1, p.sear);
        face.style.filter = `brightness(${1 - cooked * 0.35}) sepia(${cooked * 0.6})`;
      }
    }
  }

  _renderTimer(){
    const s = Math.ceil(this.timeLeft);
    this.timerEl.textContent = `0:${String(s).padStart(2, '0')}`;
    this.timerEl.classList.toggle('low', s <= 8);
  }

  _renderScore(){
    this.platedEl.textContent = `${this.plated} plated`;
    if (this.combo >= 2){
      this.comboEl.hidden = false;
      this.comboEl.textContent = `🔥 ${this.combo} combo`;
      this.comboEl.classList.remove('pulse'); void this.comboEl.offsetWidth; this.comboEl.classList.add('pulse');
    } else this.comboEl.hidden = true;
  }

  _feedback(text, cls){
    if (!this.feedbackEl) return;
    this.feedbackEl.textContent = text;
    this.feedbackEl.className = `grill-feedback show ${cls}`;
    void this.feedbackEl.offsetWidth; this.feedbackEl.classList.add('anim');
  }

  _finish(){
    if (this.finished) return;
    this.finished = true; this.active = false;
    const total = this.plated + this.burnt + this.raw;
    const rating = total ? Math.round(this.plated / total * 100) : 0;
    const crumbs = this.plated * 3 + Math.floor(this.bestCombo / 3) * 2;
    this.onFinish({ plated: this.plated, burnt: this.burnt, raw: this.raw, bestCombo: this.bestCombo, points: this.points, rating, crumbs });
  }

  unmount(){
    window.removeEventListener('keydown', this._onKey);
    if (this.el) this.el.remove();
    this.el = null;
  }
}
